import React, { useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as lodash from 'lodash';
import {
  OrderingSelectors,
  OrderingOperations,
  centsToDollarString,
  calculateChoiceSetTotals,
  tallyAndValidateChoiceSets,
  choiceSetsWithQuantities,
} from 'polygon-ordering';
import { DEVICE_TYPES } from '../constants';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { setCurrentModal } from '../thunks/setCurrentModal';
import getThemeLookup from '../selectors/getThemeLookup';
import {
  NestedChoiceSetsContext,
  INestedChoiceSetsContext,
  NestedIngredientsStages,
} from '../components/NestedChoiceSetsContext';
import Modal from '../components/Modal';
import StandardButton from '../components/StandardButton';
import RedcatImage from '../components/RedcatImage';
import ItemTopHeader from '../components/ItemTopHeader';
import ChoiceSet from '../components/ChoiceSet';
import NestedChoiceSetBanner from '../components/NestedChoiceSetBanner';
import Text from '../components/Text';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES } from '../utils/theme';

const {
  adjustOpenPurchaseChoice,
  storeOpenPurchaseMiamSelection,
  updateMiamSelections,
  removeMiamSelections,
} = OrderingOperations;

export const MIAM_MODAL_ID = 'MIAM_MODAL_ID';

const { getItems, getOpenPurchase, getCurrencySymbol } = OrderingSelectors;

const MIAMModal = () => {
  const modalParams = useAppSelector(state => state.modalParams);
  const miamChoiceSet = modalParams?.miamChoiceSet as ValidatedNestedChoiceSet;
  const miamItem = miamChoiceSet?.choices[0] as NestedChoiceWithQuantity;

  const { t } = useTranslation();
  const openPurchase = useAppSelector(getOpenPurchase);
  const p = useAppSelector(getThemeLookup);
  const dispatch = useAppDispatch();
  const deviceType = useAppSelector(state => state.deviceType);
  const isMobileDevice = deviceType === DEVICE_TYPES.MOBILE;
  const [choiceSets, setChoiceSets] = useState(miamItem.choiceSets as ValidatedChoiceSet[]);
  const items = useAppSelector(getItems);
  const rootItem = lodash.pickBy(items, i => i.id === miamItem.id)[miamItem.id];
  const { setMiamModalClose, stages } = useContext(
    NestedChoiceSetsContext,
  ) as INestedChoiceSetsContext;
  //create an unique key for each miam choiceset under nested item to operate in purchase redux
  const { choiceSets: _choiceSets } = openPurchase || {};

  const isNestedItem = _choiceSets?.some(
    (set: ChoiceSet | NestedChoiceSet) =>
      set.displayType !== 'makeameal' && 'nestedIngredients' in set && set.nestedIngredients,
  );
  const [miamParentPath, setMiamParentPath] = useState<string>(
    isNestedItem ? '' : miamChoiceSet.key,
  );
  useEffect(() => {
    if (stages) {
      setMiamParentPath(
        [stages['0'] as string, stages['1'] as string, miamChoiceSet.key].join('.'),
      );
    }
  }, [stages]);
  const currencySymbol = useAppSelector(getCurrencySymbol);
  const [totalPriceLabel, setTotalPriceLabel] = useState('');
  const choiceOrderingMethod = useAppSelector(state => state.ordering.config.choiceOrderingMethod);
  const { miam, miamQuantityMap } = openPurchase || {};
  useEffect(() => {
    if (openPurchase) {
      const { choiceSets } = openPurchase;
      if (choiceSets) {
        const parentSet = choiceSets.find(
          s => s.key === miamChoiceSet.key,
        ) as ValidatedNestedChoiceSet;
        let mSets: ValidatedChoiceSet[] = [];
        //standard item
        if (parentSet) {
          mSets = (parentSet?.choices[0] as NestedChoiceWithQuantity)
            .choiceSets as ValidatedChoiceSet[];
        } else {
          //miam choiceset under nested item
          const { choiceSelections } = openPurchase;
          if (choiceSelections && miamParentPath && choiceSelections[miamParentPath]) {
            mSets = tallyAndValidateChoiceSets(
              //@ts-ignore
              choiceSetsWithQuantities(miamItem.choiceSets, choiceSelections[miamParentPath]),
            );
          }
        }
        let miamQty = 1;
        if (miamQuantityMap && miamParentPath) {
          miamQty = miamQuantityMap[miamParentPath] || 1;
        }
        const totalPrice =
          mSets.reduce(
            (acc, s) =>
              acc +
              calculateChoiceSetTotals(s as ValidatedChoiceSet, choiceOrderingMethod || 'cheapest')
                .moneyPrice,
            0,
          ) *
            miamQty +
          miamItem.baseMoneyPrice * miamQty;
        if (totalPrice > 0) {
          const priceLabel = ` - ${currencySymbol}${centsToDollarString(totalPrice)}`;
          setTotalPriceLabel(priceLabel);
        } else {
          setTotalPriceLabel('');
        }
        if (mSets.length > 0) setChoiceSets(mSets as ValidatedChoiceSet[]);
      }
    }
  }, [openPurchase]);

  //update choiceSelections in openPurchase with the miam selections when reopening staged purchase
  useEffect(() => {
    const miamUpdate = async () => {
      if (openPurchase) {
        const { miam } = openPurchase;
        if (miamParentPath) await dispatch(updateMiamSelections({ miam, key: miamParentPath }));
      }
    };
    miamUpdate();
    return () => {
      setMiamModalClose(true);
    };
  }, [miamParentPath]);
  const collapsableChoiceSetsEnabled = useAppSelector(
    state => state.config.collapsableChoiceSetsEnabled,
  );
  const collapseOptionalChoiceSetsByDefault = useAppSelector(
    state => state.config.collapseOptionalChoiceSetsByDefault,
  );
  const [state, setState] = useState<{
    showIndicator: boolean;
    collapsedChoiceSets: { [key: string]: boolean };
  }>({
    showIndicator: false,
    collapsedChoiceSets: {},
  });

  useEffect(() => {
    const collapsedChoiceSets: { [key: string]: boolean } = {};

    choiceSets.forEach((choiceSet: ValidatedChoiceSet) => {
      const isRadioButton = choiceSet.min === 1 && choiceSet.max === 1;
      if (!choiceSet.min || (choiceSet.valid && !isRadioButton)) {
        collapsedChoiceSets[choiceSet.id] = collapsableChoiceSetsEnabled
          ? collapseOptionalChoiceSetsByDefault
          : false;
      }
    });

    setState({ ...state, collapsedChoiceSets });
  }, []);
  if (!miamItem || !openPurchase) return null;
  const imagePath = rootItem?.images.default;
  const handleClose = () => {
    dispatch(setCurrentModal(null));
  };
  const handleBackButtonClick = () => {
    dispatch(setCurrentModal(null));
  };
  const handleAddMiam = () => {
    dispatch(
      storeOpenPurchaseMiamSelection({ key: miamParentPath, selectedChoiceId: miamItem.id }),
    );
    dispatch(setCurrentModal(null));
  };

  const handleChoiceSelect = (targetSet: AdjustChoice) => {
    dispatch(
      adjustOpenPurchaseChoice({
        ...targetSet,
        parentChoiceSetId: miamParentPath,
        miam: { miamSet: miamChoiceSet, parentPath: miamParentPath || '' },
      }),
    );
  };
  const toggleCollapsed = (id: string) => {
    const { collapsedChoiceSets } = state;

    setState({
      ...state,
      collapsedChoiceSets: {
        ...collapsedChoiceSets,
        [id]: !collapsedChoiceSets[id],
      },
    });
  };

  const getQuantityHint = (set: ChoiceSet) => {
    if (!set.min) {
      return t('choiceSet.quantityHint.maximum', { max: set.max });
    }

    if (!set.max) {
      return '';
    }

    if (set.min === set.max) {
      return t('choiceSet.quantityHint.constant', { quantity: set.min });
    }

    return t('choiceSet.quantityHint.range', {
      min: set.min,
      max: set.max,
    });
  };

  const getFreeSuffix = (set: ChoiceSet) => {
    const hasFreeSuffix = set.free && (!set.max || !set.free || set.max > set.free);

    if (hasFreeSuffix) {
      return ' ' + t('choiceSet.quantityHint.freeSuffix', { free: set.free });
    }

    return '';
  };

  return (
    <Modal
      // TODO: fix this to be correct size from wireframes? (wireframe scale different to real screens?)
      containerStyle={styles.modalContainer}
      // desktopMaxWidth={1200}
      desktopMinWidth={600}
      desktopMaxHeight={600} //TODO this is the problem
      desktopMinHeight={600}
      backgroundClick={handleClose}
      handleClose={handleClose}
      handleBack={handleBackButtonClick}
      mobileContainerStyle={styles.mobileContainer}
      tallestPossible={isMobileDevice}
      enableBackButton
      headerContainerStyle={{
        justifyContent: 'flex-start',
      }}
    >
      <div>
        <RedcatImage
          alt={rootItem?.name}
          imagePath={imagePath}
          containerStyle={{
            position: 'relative',
            maxWidth: 600,
            maxHeight: isMobileDevice ? 200 : 600,
            display: 'flex',
            overflow: 'hidden',
          }}
          imageStyle={p('purchaseEditorImage', ['objectFit'])}
        />
      </div>
      <div
        style={combineStyles(
          styles.modalContentContainer,
          isMobileDevice && !imagePath ? { marginTop: 20 } : null,
        )}
      >
        <div style={styles.modalContent}>
          <div style={{ paddingLeft: 15, paddingBottom: 24 }}>
            <ItemTopHeader item={miamItem as unknown as Item} />
          </div>
          <div style={p('defaultBorder', ['borderTop'])} />
          {choiceSets.map((set, index) => {
            const quantityWarning = getQuantityHint(set) + getFreeSuffix(set);
            const collapsed = state.collapsedChoiceSets[set.id];
            const isRadioButton = set.max === 1 && set.min === 1;

            if (set.choices.length === 0) {
              return null;
            }

            return (
              <div
                key={index}
                style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <NestedChoiceSetBanner
                  choiceSet={set as ValidatedNestedChoiceSet}
                  collapsed={collapsed}
                  toggleCollapsed={toggleCollapsed}
                />

                {!collapsed && (
                  <div>
                    {!!quantityWarning && !isRadioButton && (
                      <Text
                        themeKey="choiceSetQuantityHint"
                        lines={1}
                        style={{
                          padding: '0 0 10px 20px',
                          fontSize: isMobileDevice ? 12 : 14,
                        }}
                        value={quantityWarning}
                      />
                    )}
                    <ChoiceSet
                      choiceSet={set as ValidatedChoiceSet}
                      adjustOpenPurchaseChoice={targetSet => {
                        handleChoiceSelect(targetSet);
                      }}
                      hideNegativeIngredients
                      miam={{ miamSet: miamChoiceSet, parentPath: miamParentPath || '' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div style={combineStyles(styles.bottomSection, p('defaultBorder', ['borderTop']))}>
            {miam && miamParentPath in miam ? (
              <div
                style={combineStyles(styles.bottomSection, {
                  flexDirection: isMobileDevice ? 'column' : 'row',
                })}
              >
                <StandardButton
                  label={t('button.miam.remove')}
                  onClick={() => {
                    dispatch(removeMiamSelections(miamChoiceSet.key));
                    dispatch(setCurrentModal(null));
                  }}
                  containerStyle={combineStyles(
                    styles.button,
                    p('miamButtonContainer', CONTAINER_PROPERTIES),
                    isMobileDevice && {
                      width: '90%',
                      order: 2,
                    },
                  )}
                  themeKey="miamRemoveBorderButton"
                  disabled={choiceSets.filter(s => !s.valid).length > 0}
                />
                <StandardButton
                  label={t('button.continue') + totalPriceLabel}
                  onClick={handleAddMiam}
                  containerStyle={combineStyles(
                    styles.button,
                    isMobileDevice && {
                      width: '90%',
                      order: 1,
                    },
                    p('miamButtonContainer', CONTAINER_PROPERTIES),
                  )}
                  themeKey="miamContinueButton"
                  disabled={choiceSets.filter(s => !s.valid).length > 0}
                />
              </div>
            ) : (
              <StandardButton
                label={t('button.miam.addToMeal') + totalPriceLabel}
                onClick={handleAddMiam}
                containerStyle={combineStyles(
                  styles.button,
                  p('miamButtonContainer', CONTAINER_PROPERTIES),
                )}
                themeKey="addToMealModalButton"
                disabled={choiceSets.filter(s => !s.valid).length > 0}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const MiamSection = ({
  openPurchase,
  makeamealChoiceSet,
  stages,
}: {
  openPurchase: PurchaseWithTotals;
  makeamealChoiceSet: ValidatedChoiceSet | ValidatedNestedChoiceSet;
  stages?: NestedIngredientsStages;
}) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const dispatch = useAppDispatch();
  let path = makeamealChoiceSet.key;
  if (stages) {
    path = [stages['0'] as string, stages['1'] as string, makeamealChoiceSet.key].join('.');
  }
  const { miam } = openPurchase;
  const makeamealLabel =
    miam && path in miam ? t('button.miam.customise') : makeamealChoiceSet?.name;

  return (
    <StandardButton
      label={makeamealLabel}
      containerStyle={p('miamButtonContainer', CONTAINER_PROPERTIES)}
      themeKey="miamBorderButton"
      style={{ padding: 10, flex: 1 }}
      onClick={() => {
        dispatch(
          setCurrentModal({
            modalId: MIAM_MODAL_ID,
            preserveReturnModalDetails: true,
            params: { miamChoiceSet: makeamealChoiceSet },
          }),
        );
      }}
    />
  );
};

const styles: Styles = {
  miamContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // flexDirection: 'column',
    padding: '0px 20px',
    gap: 10,
    maxWidth: '100%',
    flexWrap: 'wrap',
  },
  modalContentContainer: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    maxWidth: 600,
  },
  modalContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '30px 20px 30px 20px',
    overflow: 'scroll',
    maxWidth: '100%',
    paddingBottom: 100,
    // scrollSnapType: 'y mandatory',
    // scrollPaddingBottom: '100px',
    // paddingBottom: 100,
  },
  modalContainer: {
    //auto sized grid so that the component works correctly if the image is missing
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    overflow: 'hidden',
    fontSize: 10,
    borderRadius: 0,
    padding: 0,
    gap: 0,
  },
  mobileContainer: {
    display: 'grid',
    gridAutoFlow: 'row',
    // gridAutoRows: 'minmax(0, 1fr)',
    overflow: 'scroll',
    // For paralax image header
    borderRadius: 0,
    padding: 0,
    gap: 0,
  },
  productImage: {
    position: 'relative',
    width: '100%',
    maxHeight: '50%',
    transformOrigin: 'top',
    zIndex: -1,
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    bottom: 0,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 15,
    gap: 10,
    margin: 'auto',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    zIndex: 300,
  },
  button: {
    // position: 'absolute',
    // bottom: 10,
    width: '90%',
    // margin: 'auto',
    // left: 0,
    // right: 0,
  },
};
export default MIAMModal;
